import React from "react";
import { constants, purchaseSeatsTabUIDs } from "../config/constants";
import { useRouterExtended } from "../hooks/useRouterExtended";
import { useStorageCache } from "../hooks/useStorageCache";
import { useTeamConfig } from "../hooks/useTeamConfig";
import { useAppStore } from "../store";
import {
  cartManagerState,
  selectCartManager,
} from "../store/data/purchaseSeats/cartManager";
import {
  eventsState,
  selectEvents,
  selectEventsSelected,
} from "../store/data/purchaseSeats/events";
import { useSelectActiveEvent } from "@/shared/modules/PurchaseSeats/ChooseSeats/useSelectActiveEvent";
import { gameFiltersState } from "../store/data/gameFilters";
import { useDispatch } from "react-redux";
import { routes } from "../routes";
import { useEffectAsync } from "@/shared/base/hooks/useEffectAsync";
import { CircularProgress, Stack } from "@mui/material";
import { useCartNavigationHelper } from "@/shared/modules/PurchaseSeats/useCartBackButtonHelper";
import { globalConfig } from "../config/global_config";
import { usePerksState } from "@/shared/modules/PurchaseSeats/SelectPerks/usePerksManager";

export function CartCacheProvider(props: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const router = useRouterExtended();
  const teamConfig = useTeamConfig();
  const isCartCacheEnabled = teamConfig?.purchaseSeatConfigs?.isCartCacheEnabled; // prettier-ignore
  const cartCache = useStorageCache(constants.cartCacheKey);

  // hooks
  const { navigateToDefaultPage } = useCartNavigationHelper();

  // states
  const gameFilters = useAppStore((s) => s.data.gameFilters);
  const events = useAppStore(selectEvents);
  const selectedEvents = useAppStore(selectEventsSelected);
  const { activeEvent } = useSelectActiveEvent();
  const cartState = useAppStore(selectCartManager);
  const { perkParents, set: setPerksState } = usePerksState();
  const purchaseSeatTab = router?.query?.purchaseSeatTab;
  const [isLoading, setIsLoading] = React.useState(true);

  const createCartCacheEntry = () => {
    return {
      purchaseSeatTab,
      gameFilters,
      activeEvent,
      cartState,
      perkParents,
      events,
      selectedEvents,
    };
  };

  const isCartRoute = () => {
    return (
      purchaseSeatTab == purchaseSeatsTabUIDs.chooseSeats ||
      purchaseSeatTab == purchaseSeatsTabUIDs.selectPerks ||
      purchaseSeatTab == purchaseSeatsTabUIDs.checkout
    );
  };

  const persistCartActivity = () => {
    try {
      if (isCartRoute() && (cartState?.cartId || cartState?.sessionId)) {
        const cacheEntry = createCartCacheEntry();
        cartCache.set(teamConfig.teamSchema, cacheEntry);
      }
      if (!isCartRoute()) {
        cartCache.delete(teamConfig.teamSchema);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isCacheExpired = (cacheMeta: any) => {
    const expiry = globalConfig.cartCacheExpiry;
    const now = Date.now();
    const cacheTimestamp = cacheMeta.timestamp;
    return now - cacheTimestamp > expiry;
  };

  const loadAppWithCartCache = async () => {
    try {
      const cacheMeta = cartCache.getEntryMeta(teamConfig.teamSchema);
      if (!cacheMeta || !cacheMeta?.value) return;

      const cacheEntry = cacheMeta?.value;

      dispatch(eventsState.actions.setEvents(cacheEntry?.events));
      dispatch(eventsState.actions.setEventsSelected(cacheEntry?.selectedEvents)); // prettier-ignore
      dispatch(gameFiltersState.actions.set(cacheEntry?.gameFilters));
      setPerksState({ perkParents: cacheEntry?.perkParents });
      dispatch(cartManagerState.actions.set(cacheEntry?.cartState));

      const isExpired = isCacheExpired(cacheMeta);
      if (isExpired) return await navigateToDefaultPage();

      return await router.pushSchema({
        pathname: routes.purchaseSeats,
        query: {
          eventCode: cacheEntry?.activeEvent?.eventCode,
          purchaseSeatTab: cacheEntry?.purchaseSeatTab,
        },
      });
    } catch (error) {
      console.log("error", error);
      await navigateToDefaultPage();
    } finally {
      setIsLoading(false);
      cartCache.delete(teamConfig.teamSchema);
    }
  };

  React.useEffect(() => {
    if (!isCartCacheEnabled) return;
    persistCartActivity();
  }, [
    purchaseSeatTab,
    gameFilters?.isInitialSeatCountHydrated,
    gameFilters?.seatCount,
    gameFilters?.priceRange?.data?.min,
    gameFilters?.priceRange?.data?.max,
    gameFilters?.priceRange?.selected[0],
    gameFilters?.priceRange?.selected[1],
    gameFilters?.accessibility?.isAda,
    gameFilters,
    activeEvent,
    cartState,
    events,
    selectedEvents,
    perkParents,
  ]);

  useEffectAsync({
    deps: [],
    effect: async () => {
      if (!isCartCacheEnabled) return setIsLoading(false);
      await loadAppWithCartCache();
    },
    onEffectError: (error) => {
      console.log("error", error);
    },
  });

  if (isLoading) {
    return (
      <Stack
        width={"100%"}
        height={"100vh"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <CircularProgress size={"64px"} color="primary" />
      </Stack>
    );
  } else {
    return props.children;
  }
}
